import BottomSheet from "../../../../components/ui/BottomSheet";
import Icon from "../../../../components/ui/Icon";
import Photo from "../../../../components/ui/Photo";
import { IExtra, IProduct } from "../../../../classes/interfaces";
import { formatBRL } from "../../../../helpers/currency.helper";
import { formatAmount } from "../../../../classes/strings";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import OptionRow from "./OptionRow";
import StepGroupHeader from "./StepGroupHeader";
import {
  PhotoWrap, CloseButton, Body, Header, Name, Description, PriceRow, Price, OldPrice,
  ExtrasSection, ExtrasBody, OptionsCard, ObsSection, ObsLabel, ObsHint, Textarea, Footer,
  AmountStepper, StepButton, AmountValue, AddButton,
} from "./ProductSheet.styles";

interface ProductSheetProps {
  isOpen: boolean;
  product: IProduct | null;
  onClose: () => void;
  onChangeObs: (obs: string) => void;
  onMoreProduct: () => void;
  onLessProduct: () => void;
  onMoreExtra: (extra: IExtra) => void;
  onLessExtra: (extra: IExtra) => void;
  onAdd: () => void;
  /** Cardápio-vitrine (/menu): só os detalhes — sem quantidade, observação nem "Adicionar". */
  readOnly?: boolean;
}

export default function ProductSheet({
  isOpen, product, onClose, onChangeObs, onMoreProduct, onLessProduct, onMoreExtra, onLessExtra, onAdd,
  readOnly = false,
}: ProductSheetProps) {
  const desktop = useIsDesktopViewport();
  const hasPromo = !!product?.promotionalPrice && product.promotionalPrice > 0;

  return (
    <BottomSheet open={isOpen} onClose={onClose} maxHeight="90%" centered={desktop}>
      <Body>
        <PhotoWrap>
          <Photo src={product?.imageUrl} label={(product?.name || "produto").toLowerCase()} radius={0} />
          <CloseButton aria-label="Fechar" onClick={onClose}>
            <Icon name="x" size={18} strokeWidth={2.2} />
          </CloseButton>
        </PhotoWrap>

        <Header>
          <Name>{product?.name || "Sem título"}</Name>
          {product?.description && <Description>{product.description}</Description>}
          <PriceRow>
            {hasPromo && <OldPrice>de {formatBRL(product!.price)}</OldPrice>}
            <Price>{formatBRL(hasPromo ? product!.promotionalPrice : product?.price || 0)}</Price>
          </PriceRow>
        </Header>

        {product?.extras && product.extras.length > 0 && (
          <ExtrasSection>
            {/* mesmo padrão de grupo dos sabores/etapas (StepGroupHeader + OptionsCard) */}
            <StepGroupHeader
              index={1}
              title="Adicionais"
              subtitle={readOnly ? "Opcional" : "Opcional · adicione à vontade"}
              required={false}
              done={!readOnly && product.extras.some((e) => (e.amount || 0) > 0)}
            />
            <ExtrasBody>
              <OptionsCard>
                {product.extras.map((extra) => (
                  <OptionRow
                    key={extra.id}
                    name={extra.name}
                    price={extra.price}
                    qty={extra.amount}
                    max={extra.max > 0 ? extra.max : 99}
                    onChange={(v) => (v > (extra.amount || 0) ? onMoreExtra(extra) : onLessExtra(extra))}
                    readOnly={readOnly}
                  />
                ))}
              </OptionsCard>
            </ExtrasBody>
          </ExtrasSection>
        )}

        {!readOnly && (
          <ObsSection>
            <ObsLabel>
              Alguma observação?
              <ObsHint>opcional</ObsHint>
            </ObsLabel>
            {/* Uncontrolled: avoids cursor-jump on parent re-render. onChangeObs still
                propagates every keystroke up to the caller; BottomSheet remounts this
                on each open, so a fresh product's obs is seeded correctly via defaultValue. */}
            <Textarea
              defaultValue={product?.obs || ""}
              onChange={(e) => onChangeObs(e.target.value)}
              placeholder="Ex.: sem cebola, ponto da carne…"
              rows={3}
            />
          </ObsSection>
        )}
      </Body>

      {!readOnly && (
        <Footer>
          <AmountStepper>
            <StepButton aria-label="Diminuir quantidade" onClick={onLessProduct}>
              <Icon name="minus" size={17} strokeWidth={2.4} />
            </StepButton>
            <AmountValue>{formatAmount(product?.amount || 0, product?.isUnitKg ?? false)}</AmountValue>
            <StepButton aria-label="Aumentar quantidade" onClick={onMoreProduct}>
              <Icon name="plus" size={17} strokeWidth={2.4} />
            </StepButton>
          </AmountStepper>
          <AddButton onClick={onAdd}>Adicionar {formatBRL(product?.total || 0)}</AddButton>
        </Footer>
      )}
    </BottomSheet>
  );
}
